/**
 * Guest side of the standing-latency drain: maps a host `standingLatencyDrain` request onto
 * the ReceiverLatencyHintController and answers with what the receiver reports afterwards.
 *
 * `reassert` re-writes the last target so an engine whose timing module ignored an unchanged
 * hint re-evaluates it. `pulse` writes a small non-zero target and restores the previous one
 * on the next macrotask; the result carries the read-back AFTER the restore, so a pulse that
 * left the target raised is visible on the host. Neither touches the connection, the track
 * or the decoder — this handler has no handle to any of them.
 *
 * Every request gets exactly one result, even when the receiver is detached or a write throws
 * (`applied: false`, `error`): the host's verify window must not wait on silence.
 */

import type { ReceiverLatencyHintController } from '../receiverHints/receiverLatencyHintController';
import type { ReceiverHintReadback } from '../receiverHints/receiverHintContracts';
import {
  DRAIN_PULSE_TARGET_MS,
  STANDING_LATENCY_DRAIN_RESULT_MESSAGE_TYPE,
  isStandingLatencyDrainRequest,
  type StandingLatencyDrainRequest,
  type StandingLatencyDrainResult,
} from './drainContracts';

export type DrainRequestHandlerDeps = {
  controller: ReceiverLatencyHintController;
  /** Puts a result on the datachannel to the host. */
  send: (json: string) => void;
  /** Target a `pulse` writes when the request carries none. */
  pulseTargetMs?: number;
};

/** What a detached controller reads back: nothing, applied never. */
const EMPTY_READBACK: ReceiverHintReadback = { jitterBufferTargetMs: null, playoutDelayHintMs: null, appliedAtMs: 0 };

export class DrainRequestHandler {
  private readonly pulseTargetMs: number;

  constructor(private readonly deps: DrainRequestHandlerDeps) {
    this.pulseTargetMs = deps.pulseTargetMs ?? DRAIN_PULSE_TARGET_MS;
  }

  /** Returns true when `data` was a drain request (handled), false otherwise. */
  handle(data: string): boolean {
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return false;
    }
    if (!isStandingLatencyDrainRequest(parsed)) return false;
    if (parsed.action === 'pulse') void this.pulse(parsed);
    else this.reassert(parsed);
    return true;
  }

  private reassert(request: StandingLatencyDrainRequest): void {
    let readback: ReceiverHintReadback | null = null;
    let error: string | undefined;
    try {
      readback = this.deps.controller.reassert();
      if (readback === null) error = 'no receiver attached or no target applied';
    } catch (thrown) {
      error = String(thrown);
    }
    this.reply(request, readback, error);
  }

  private async pulse(request: StandingLatencyDrainRequest): Promise<void> {
    const targetMs = request.pulseTargetMs ?? this.pulseTargetMs;
    let readback: ReceiverHintReadback | null = null;
    let error: string | undefined;
    try {
      readback = await this.deps.controller.pulse(targetMs);
      if (readback === null) error = 'no receiver attached or pulse target rejected';
    } catch (thrown) {
      error = String(thrown);
    }
    this.reply(request, readback, error);
  }

  private reply(request: StandingLatencyDrainRequest, readback: ReceiverHintReadback | null, error: string | undefined): void {
    const result: StandingLatencyDrainResult = {
      type: STANDING_LATENCY_DRAIN_RESULT_MESSAGE_TYPE,
      seq: request.seq,
      action: request.action,
      applied: readback !== null && error === undefined,
      readback: readback ?? this.deps.controller.readback() ?? EMPTY_READBACK,
      ...(error === undefined ? {} : { error }),
    };
    this.deps.send(JSON.stringify(result));
  }
}
